import { InitialAuthErrorCode } from '@/features/auth/constants/errors'
import { InitialAuthThunkRejectExtra } from '@/features/auth/types'
import { useAppDispatch } from '@/hooks/redux'
import { setInitialAuth } from '@/redux/reducers/auth'
import { isThunkRejectValue } from '@/redux/utils'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface AuthLoadError {
    title: string
    message: string
}

interface UseAuthLoadedResult {
    loaded: boolean
    error: AuthLoadError | null
    retry?: () => Promise<void>
}

export const useAuthLoaded = (): UseAuthLoadedResult => {
    const dispatch = useAppDispatch()

    const { t } = useTranslation()

    const [loaded, setLoaded] = useState(false)
    const [errorCode, setErrorCode] = useState<InitialAuthErrorCode | undefined>()

    const load = useCallback(async () => {
        setLoaded(false)
        setErrorCode(undefined)

        try {
            await dispatch(setInitialAuth()).unwrap()
        } catch (e) {
            if (!isThunkRejectValue<InitialAuthThunkRejectExtra>(e)) throw e

            setErrorCode(e.extra?.code)
        } finally {
            setLoaded(true)
        }
    }, [dispatch])

    useEffect(() => {
        load()
    }, [load])

    const canRetry = errorCode === InitialAuthErrorCode.NoInternet

    const createError = (code: InitialAuthErrorCode): AuthLoadError => {
        return {
            title: t('errors.errorHappenedTitle'),
            message:
                code === InitialAuthErrorCode.NoInternet
                    ? t('errors.missingNetworkConnection')
                    : t('errors.tryRestartApp'),
        }
    }

    return {
        loaded,
        error: errorCode !== undefined ? createError(errorCode) : null,
        retry: canRetry ? load : undefined,
    }
}
